import { useEffect, useState } from "react";
import { fetchAlternatives } from "../api/transactions";
import type { AccountSuggestion } from "../components/AccountSelector";

export interface Alternatives {
  suggestions: AccountSuggestion[];
  /** Every account the selector may offer; empty until the server answers. */
  accounts: string[];
}

const NONE: Alternatives = { suggestions: [], accounts: [] };

/** An answer and the card it was fetched for, so a closed or other card shows none. */
interface Fetched extends Alternatives {
  itemId: number;
}

/**
 * The selector's suggestions for the open card, fetched when it opens. On
 * a failed read the selector falls back to every account with no suggestion.
 */
export function useAlternatives(
  clientId: number,
  itemId: number | null,
  fallbackAccounts: string[],
): Alternatives {
  const [fetched, setFetched] = useState<Fetched | null>(null);

  useEffect(() => {
    if (itemId === null) return;
    let cancelled = false;
    fetchAlternatives(clientId, itemId)
      .then((data) => {
        if (cancelled) return;
        setFetched({
          itemId,
          suggestions: data.alternatives.map((a) => ({ account: a.account, reason: a.reason })),
          accounts: data.all_accounts,
        });
      })
      .catch(() => {
        if (!cancelled) setFetched({ itemId, suggestions: [], accounts: fallbackAccounts });
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, itemId, fallbackAccounts]);

  return fetched !== null && fetched.itemId === itemId ? fetched : NONE;
}
